import type { App } from 'vue'
import { init, replayIntegration, type ErrorEvent } from '@sentry/vue'
import { info, isGroupEnabled } from '@/lib/logger'
import { getEnv } from '@/lib/environment'

type SentryConfig = {
  /**
   * *(AI-generated)*
   * The Vue app instance
   */
  app: App
  /** An optional function that allows filtering or modifying events before they are sent to Sentry.
   * Return the event to send it, or null to drop it. */
  beforeSend?: (event: ErrorEvent) => ErrorEvent | null
  /**
   * *(AI-generated)*
   * The sample rate for session-long replays. 1.0 will record all sessions and 0 will record none.
   */
  replaysSessionSampleRate?: number
  /**
   * *(AI-generated)*
   * The sample rate for sessions that has had an error occur. This is independent of sessionSampleRate. 1.0 will record all sessions and 0 will record none.
   */
  replaysOnErrorSampleRate?: number
}

export const PRODUCTION_SAMPLE_RATE = 0.01
export const DEVELOPMENT_SAMPLE_RATE = 1.0

/**
 * Builds a `beforeSend` callback that applies the environment's sample rate
 * manually, but always keeps events whose `feature` tag is in
 * `alwaysKeepFeatures`.
 *
 * Sentry's top-level `sampleRate` drops events *before* `beforeSend` runs, so
 * a feature that needs full-fidelity reporting can only be exempted by moving
 * sampling into `beforeSend` — which is why `initSentry` forces
 * `sampleRate: 1` whenever a `beforeSend` is supplied (see below). At the
 * production rate of 1%, a feature left under the default sampling is
 * effectively invisible for alerting.
 *
 * @param alwaysKeepFeatures `feature` tag values exempt from sampling.
 */
export function makeSampler(
  alwaysKeepFeatures: string[]
): (event: ErrorEvent) => ErrorEvent | null {
  return function sampleEvent(event) {
    const feature = event.tags?.feature
    if (typeof feature === 'string' && alwaysKeepFeatures.includes(feature)) {
      return event
    }

    const sampleRate =
      getEnv() === 'production'
        ? PRODUCTION_SAMPLE_RATE
        : DEVELOPMENT_SAMPLE_RATE

    // Returns:
    // - The event: if random number is less than sample rate
    // - null: if random number is greater than sample rate (event is dropped)
    return Math.random() < sampleRate ? event : null
  }
}

/**
 * *(AI-generated)*
 * Initializes Sentry error tracking with the given configuration options.
 */
export function initSentry({
  app,
  beforeSend = undefined,
  replaysSessionSampleRate = 0,
  replaysOnErrorSampleRate = 1,
}: SentryConfig) {
  const env = getEnv()
  const defaultRate =
    env === 'production' ? PRODUCTION_SAMPLE_RATE : DEVELOPMENT_SAMPLE_RATE

  // sample rate handled manually in beforeSend callback arg if passed
  const sampleRate = beforeSend ? 1 : defaultRate

  const enabled = process.env.NODE_ENV !== 'development'

  if (!enabled) {
    info('Sentry', 'Sentry disabled in development mode')
    return
  }
  info('Sentry', 'Sentry initialized', { env, sampleRate })
  init({
    app,
    dsn: 'https://27a37fbcd39cba0adee6c085d0dfedad@o240024.ingest.us.sentry.io/4511175122223104',
    integrations: [replayIntegration()],
    debug: isGroupEnabled('Sentry'),
    release: process.env.SENTRY_RELEASE,
    environment: env,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,

    maxBreadcrumbs: 50,
    sampleRate: sampleRate,

    beforeSend,
    beforeBreadcrumb: function (breadcrumb) {
      // filter out noisy breadcrumb http requests
      if (
        breadcrumb.type === 'http' &&
        (breadcrumb?.data?.url.includes('split.io') ||
          breadcrumb?.data?.url.includes('tileserver') ||
          // Remove urls with JWT tokens
          breadcrumb?.data?.url.includes('token'))
      ) {
        return null
      }
      return breadcrumb
    },
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      // Cacaoweb
      /127\.0\.0\.1:4001\/isrunning/i,
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  })
}
